import React, { useEffect, useMemo, useState } from 'react';
import { Store } from '~/lib/data-contract/salesforce';
import type { StoreDeliveryAddressPayload } from '~/features/checkout/models/fields';
import {
    ClickAndCollectStoresWrapper,
    FormTitle,
    StoreAdress,
    StoreContent,
    StoreTitle,
    StoreWrapper,
    StoresCloseIcon,
    StoresCloseWrapper,
    StoresWrapper,
    StyledStoreSearchInput,
} from './styled';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { Button } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import Loading from '~/app/(preference-center)/preference-center/[id]/loading';
import { useStores } from '~/features/checkout/hooks/useStores';
import { trimCityArea } from '~/shared/utils';
import { findClosestStores } from '~/shared/utils/findClosestStores';
import type { Coordinates } from '~/shared/utils/coordinates';
import Flex from '~/shared/components/Flex';
import { theme } from '~/theme';
import { useCustomerStore } from '~/features/commerce-api/hooks/useCustomerStore';
import { COORDINATES_DENMARK } from '~/shared/constants';
import { prioritizeStoresByPinnedIds } from '~/shared/utils';
import { useCitySearch } from '~/shared/hooks/useCitySearch/useCitySearch';

type Props = {
    handleCloseDrawer: () => void;
    showStepDrawer: boolean;
    onClickAndCollectStoreSelectRequest: (store: Store) => boolean;
};

export const ClickAndCollectStores = ({
    handleCloseDrawer,
    showStepDrawer,
    onClickAndCollectStoreSelectRequest,
}: Props) => {
    const { translate } = useTranslation();
    const { setDeliveryAddress, zipCode } = useCheckoutState();
    const { customer, isStorePersonnel } = useCustomerStore(); // Need to add this
    const [searchQuery, setSearchQuery] = useState(zipCode);
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [nearbyStores, setNearbyStores] = useState<Store[]>([]);

    const saveDeliveryInformation = (deliveryOption: Store) => {
        if (onClickAndCollectStoreSelectRequest(deliveryOption)) {
            return;
        }

        const deliveryAddress: StoreDeliveryAddressPayload = {
            _type: 'store',
            id: deliveryOption.id,
            name: deliveryOption.name,
            address1: deliveryOption.address1,
            address2: deliveryOption.address2 ?? '',
            city: deliveryOption.city,
            countryCode: deliveryOption.countryCode,
            postalCode: deliveryOption.postalCode ?? deliveryOption.postal_code ?? '',
        };

        setDeliveryAddress(deliveryAddress);
        handleCloseDrawer();
    };

    const { data: storesResponse, isLoading: isLoadingStores } = useStores({
        ...COORDINATES_DENMARK,
        maxDistance: 10000,
        count: 200,
        start: 0,
    });

    const {
        data: cityData,
        isFetching: isFetchingCoordinates,
        isLoading: isLoadingCoordinates,
    } = useCitySearch(submittedQuery);
    const coordinates = useMemo<Coordinates | undefined>(() => {
        const latitude = cityData?.[0]?.latitude;
        const longitude = cityData?.[0]?.longitude;

        if (typeof latitude !== 'number' || typeof longitude !== 'number') return undefined;

        return { latitude, longitude };
    }, [cityData]);

    const storesWithClickAndCollect = useMemo(
        () =>
            Array.isArray(storesResponse?.data)
                ? storesResponse?.data.filter((store) => {
                      // Regular click & collect stores
                      const isClickCollectStore = !!store.c_clickCollectEnabled;

                      // Check if this is the iInstore user's store
                      // lower case email in case of inconsistent email input letter casing
                      const isUsersStore =
                          isStorePersonnel &&
                          store.email?.toLowerCase() === customer?.email?.toLowerCase();

                      // Show store if either condition is true
                      return isClickCollectStore || isUsersStore;
                  })
                : undefined,
        [storesResponse?.data, isStorePersonnel, customer?.email],
    );

    const sortedStores = useMemo(
        () =>
            storesWithClickAndCollect?.sort((a, b) => {
                const cityA = trimCityArea(a?.city).toUpperCase();
                const cityB = trimCityArea(b?.city).toUpperCase();
                if (cityA < cityB) {
                    return -1;
                }
                if (cityA > cityB) {
                    return 1;
                }
                // cities are equal, sort by weight
                return a?.c_weight - b?.c_weight;
            }),

        [storesWithClickAndCollect],
    );

    const handleStoreSearch = () => {
        if (!searchQuery?.length) return;
        setSubmittedQuery(searchQuery);
    };

    const noSearchResultsText = translate('message.noSearchResultsFound');

    useEffect(() => {
        if (!submittedQuery || isFetchingCoordinates) return;

        if (coordinates && sortedStores) {
            const result = prioritizeStoresByPinnedIds(
                findClosestStores(coordinates, sortedStores),
            );

            setNearbyStores(result);
            setSearchError('');
            return;
        }

        setNearbyStores([]);
        setSearchError(noSearchResultsText);
    }, [coordinates, isFetchingCoordinates, noSearchResultsText, sortedStores, submittedQuery]);

    const handleKeyDownOnSearch = (event: { key: string; preventDefault: () => void }) => {
        if (event.key === 'Enter' && searchQuery?.length > 2) {
            event.preventDefault();
            handleStoreSearch();
        }
    };

    useEffect(() => {
        if (!isLoadingStores && !isFetchingCoordinates && showStepDrawer) {
            handleStoreSearch();
        }
    }, [isLoadingStores, isFetchingCoordinates, showStepDrawer]);

    const storeList = (
        <StoresWrapper>
            {Array.isArray(nearbyStores) ? (
                nearbyStores.map((deliveryOption) => {
                    return (
                        <StoreWrapper
                            id={deliveryOption.id}
                            key={deliveryOption.id}
                            data-testid="storewrapper-locator"
                        >
                            <StoreContent>
                                <StoreTitle>{deliveryOption?.name || ''}</StoreTitle>
                                <StoreAdress>
                                    <span>{deliveryOption?.address1}</span>
                                    <span>
                                        {deliveryOption?.zip ?? deliveryOption?.postalCode},{' '}
                                        {deliveryOption?.city}
                                    </span>
                                    <span>{deliveryOption?.address2}</span>
                                </StoreAdress>
                            </StoreContent>

                            <Button
                                small
                                onClick={() => saveDeliveryInformation(deliveryOption)}
                                data-testid="select-store-button"
                            >
                                {translate('checkout.delivery.select')}
                            </Button>
                        </StoreWrapper>
                    );
                })
            ) : (
                <StoreWrapper>{searchError}</StoreWrapper>
            )}
        </StoresWrapper>
    );

    return (
        <ClickAndCollectStoresWrapper>
            <StoresCloseWrapper>
                <StoresCloseIcon
                    data-testid="close-stores-icon"
                    onClick={handleCloseDrawer}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCloseDrawer();
                        }
                    }}
                />
            </StoresCloseWrapper>
            <FormTitle>{translate('checkout.checkout.delivery.location')}</FormTitle>
            <Flex alignItems="center" justifyContent="space-between">
                <StyledStoreSearchInput
                    value={searchQuery}
                    setValue={setSearchQuery}
                    disabled={isLoadingStores}
                    onKeyDown={handleKeyDownOnSearch}
                />
                <Button
                    disabled={searchQuery.length < 3 || isLoadingCoordinates}
                    css={{ marginLeft: theme.fontSizes['exs'] }}
                    onClick={handleStoreSearch}
                >
                    {translate('findStore.searchButton')}
                </Button>
            </Flex>
            {isLoadingStores ? <Loading /> : storeList}
        </ClickAndCollectStoresWrapper>
    );
};
